import React, { useState, useLayoutEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
    Box, 
    Typography, 
    InputBase, 
    Paper,
    IconButton,
    Collapse
} from '@mui/material';
import { 
    Container, 
    BreadcrumbWrapper, 
    BreadcrumbInner, 
    BreadcrumbContent, 
    BreadcrumbLink, 
    BreadcrumbSeparator, 
    BreadcrumbCurrent,
    PageHeader,
    MainTitle,
    SearchWrapper,
    ContentInner,
    SectionTitle,
    StyledAccordion,
    AccordionHeader,
    QuestionText,
    AccordionContent,
    AnswerText
} from './styles/FAQStyles';
import { UpdateAppSubHeaderTitle } from '../../redux/actions/UiActions';
import { sanitizeHTML } from '../../utilities/functions/StringUtil';
import { getAllQuestions, getAllServices } from '../../api/ListOfServicesPerCategory';
import { localDirections } from '../AllServices/AllServicesConstants';
import CenterLoading from '../../components/CenterLoading/CenterLoading';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';

function FAQ() {
    const dispatch = useDispatch();
    const history = useHistory();
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedId, setExpandedId] = useState(null);

    const { data: allQuestionsData, isLoading: isLoadingQuestions } = useQuery(["allQuestionsData"], () => getAllQuestions());
    const { data: listOfServices, isLoading: isLoadingServices } = useQuery(["listOfServices"], () => getAllServices());

    useLayoutEffect(() => {
        dispatch(UpdateAppSubHeaderTitle(''));
    }, [dispatch]);

    const handleToggle = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    const getCategoryName = (key) => {
        if (key === 'todosServicios') return 'General';
        
        // Find direction by wordpressKey
        const direction = localDirections.find(d => d.wordpressKey === key);
        if (!direction) return key;

        // Find service category by direction id
        const category = listOfServices?.find(c => c.id === direction.id);
        return category ? category.name : direction.title;
    };

    const isLoading = isLoadingQuestions || isLoadingServices;

    const filteredData = useMemo(() => {
        if (!allQuestionsData) return {};
        const result = {};
        
        Object.keys(allQuestionsData).forEach(categoryKey => {
            const filteredQuestions = allQuestionsData[categoryKey].filter(q => 
                q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                q.answer.toLowerCase().includes(searchTerm.toLowerCase())
            );
            
            if (filteredQuestions.length > 0) {
                result[categoryKey] = filteredQuestions;
            }
        });
        
        return result;
    }, [allQuestionsData, searchTerm]);

    if (isLoading) return <CenterLoading />;

    return (
        <Container>
            <BreadcrumbWrapper>
                <BreadcrumbInner>
                    <BreadcrumbContent>
                        <BreadcrumbLink onClick={() => history.push('/')}>Inicio</BreadcrumbLink>
                        <BreadcrumbSeparator>/</BreadcrumbSeparator>
                        <BreadcrumbCurrent>Preguntas Frecuentes</BreadcrumbCurrent>
                    </BreadcrumbContent>
                </BreadcrumbInner>
            </BreadcrumbWrapper>

            <PageHeader>
                <MainTitle>Preguntas Frecuentes</MainTitle>
                <SearchWrapper>
                    <Paper
                        elevation={0}
                        sx={{ 
                            p: '4px 12px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            borderRadius: '50px',
                            border: '1px solid #E2E8F0',
                            backgroundColor: 'white',
                            '&:focus-within': {
                                borderColor: '#202A5D',
                                boxShadow: '0 0 0 3px rgba(32, 42, 93, 0.1)'
                            }
                        }}
                    >
                        <SearchIcon sx={{ color: '#94A3B8', mr: 1 }} />
                        <InputBase
                            sx={{ ml: 1, flex: 1, fontFamily: 'inherit' }}
                            placeholder="Busca una pregunta o palabra clave..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Paper>
                </SearchWrapper>
            </PageHeader>

            <ContentInner>
                {Object.keys(filteredData).length > 0 ? (
                    Object.keys(filteredData).map((categoryKey) => (
                        <Box key={categoryKey} sx={{ mb: 4 }}>
                            <SectionTitle>
                                {getCategoryName(categoryKey)}
                            </SectionTitle>
                            
                            {filteredData[categoryKey].map((item, idx) => {
                                const id = `${categoryKey}-${idx}`;
                                const isExpanded = expandedId === id;
                                
                                return (
                                    <StyledAccordion key={id} expanded={isExpanded}>
                                        <AccordionHeader onClick={() => handleToggle(id)}>
                                            <QuestionText expanded={isExpanded}>
                                                {item.question}
                                            </QuestionText>
                                            <ExpandMoreIcon 
                                                sx={{ 
                                                    transition: 'transform 0.3s',
                                                    transform: isExpanded ? 'rotate(180deg)' : 'none',
                                                    color: isExpanded ? '#202A5D' : '#94A3B8'
                                                }} 
                                            />
                                        </AccordionHeader>
                                        <AccordionContent expanded={isExpanded}>
                                            <AnswerText dangerouslySetInnerHTML={{ __html: sanitizeHTML(item.answer) }} />
                                        </AccordionContent>
                                    </StyledAccordion>
                                );
                            })}
                        </Box>
                    ))
                ) : (
                    <Box sx={{ textAlign: 'center', py: 10 }}>
                        <HelpCenterIcon sx={{ fontSize: 64, color: '#E2E8F0', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            No encontramos resultados para tu búsqueda.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Intenta con otras palabras clave.
                        </Typography>
                    </Box>
                )}
            </ContentInner>
        </Container>
    );
}

export default FAQ;
